import React from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CodeIcon from "@mui/icons-material/Code";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

import Graph from "graphology";
import forceAtlas2 from "graphology-layout-forceatlas2";

import { useAppSelector, useAppDispatch } from "../../app/hooks";
import { selectUser } from "../auth/authSlice";
import { setNavLocation, selectNavLocation } from "../navigation/navigationSlice";
import {
  apiFeatureTrees, apiListAnchors, apiReanchor, apiListBundles, apiClearFeatures,
  apiGetGraphSettings, apiSaveGraphSettings, apiMethodCode, apiEdgeGuards,
} from "../../services/StructuresAPI";

// sigma@3's shipped .d.ts uses TS 4.7+ syntax this project's TS can't parse;
// load the runtime via require and treat it as `any` (same as RepoNetworkModal).
// eslint-disable-next-line @typescript-eslint/no-var-requires
const SigmaModule: any = require("sigma");
const Sigma: any = SigmaModule.default || SigmaModule;

// FEATURE-mode palette (mirrors the overlay encoding):
//   anchored launch (curated) = gold, auto launch (is_feature) = coral,
//   flow node = teal, flow edge = coral, selected = amber.
const COLOR_ANCHORED = "#e0a82e";
const COLOR_LAUNCH = "#d85a30";
const COLOR_FLOW = "#1d9e75";
const COLOR_EDGE = "#9a4f2e";
const COLOR_SELECTED = "#f59e0a";
const COLOR_UNBUNDLED = "#3a4055";
// Palette for manually colouring a node (shared with the STRUCTURAL view via the
// per-repo node_colors map, keyed by node id).
const GROUP_COLORS = ["#ef4444", "#f97316", "#eab308", "#22c55e", "#14b8a6", "#3b82f6", "#8b5cf6", "#ec4899", "#94a3b8"];
// Fallback palette for bundles created without an explicit color.
const BUNDLE_FALLBACK = ["#7f77dd", "#1d9e75", "#d4537e", "#378add", "#ba7517", "#1d9e9e"];

// Keep the spine readable: don't flatten the entire transitive program.
const MAX_DEPTH = 4;
const MAX_NODES = 800;

// Label renderer (matches STRUCTURAL): unselected nodes get light-grey text beside
// the node; the SELECTED node (data.sel, set by the reducer) gets a white box with
// black text so it's clearly readable. sigma's default light-on-dark is illegible.
function drawNodeLabel(context: any, data: any, settings: any) {
  const label = data.label;
  if (typeof label !== "string" || !label) return;
  // Launch points get the full label size; flow nodes a bit smaller.
  const size = data.launch ? (settings.labelSize || 12) : 10;
  context.font = `${settings.labelWeight || "normal"} ${size}px ${settings.labelFont || "monospace"}`;
  const lx = data.x + data.size + 4;
  const ly = data.y;
  if (data.sel) {
    const tw = context.measureText(label).width;
    const padX = 5;
    const padY = 3;
    context.fillStyle = "#ffffff";
    context.fillRect(lx - padX, ly - size / 2 - padY, tw + padX * 2, size + padY * 2);
    context.fillStyle = "#000000";
  } else {
    context.fillStyle = "#c8cdd8"; // light grey
  }
  context.textAlign = "left";
  context.textBaseline = "middle";
  context.fillText(label, lx, ly);
}

// Hover: always a white box + black text so any hovered node is readable.
function drawNodeHover(context: any, data: any, settings: any) {
  const label = data.label;
  if (typeof label !== "string" || !label) return;
  const size = settings.labelSize || 12;
  context.font = `${settings.labelWeight || "normal"} ${size}px ${settings.labelFont || "monospace"}`;
  const tw = context.measureText(label).width;
  const padX = 5;
  const padY = 3;
  const lx = data.x + data.size + 4;
  const ly = data.y;
  context.fillStyle = "#ffffff";
  context.fillRect(lx - padX, ly - size / 2 - padY, tw + padX * 2, size + padY * 2);
  context.fillStyle = "#000000";
  context.textAlign = "left";
  context.textBaseline = "middle";
  context.fillText(label, lx, ly);
}

interface RawNode {
  method_id: number;
  name: string;
  class_name?: string | null;
  file_path?: string | null;
  line_number?: number;
  is_external?: boolean;
  children?: RawNode[];
}

interface Anchor {
  id: number;
  owner_kind: string;
  owner_id: number;
  file_path: string;
  symbol_name?: string | null;
  symbol_kind?: string | null;
  class_name?: string | null;
  source: string;
  resolved_method_id?: number | null;
  drift_state: string;
}

interface DriftCounts {
  ok: number;
  moved: number;
  missing: number;
  ambiguous: number;
  unresolved: number;
}

interface FeatureNode {
  id: string;
  label: string;
  filePath?: string | null;
  line?: number;
  isLaunch: boolean;
  anchored: boolean;
}

interface BundleInfo {
  id: number;
  name: string;
  slug: string;
  color?: string | null;
  rules: { rule_kind: string; pattern: string }[];
  file_paths: string[];
  file_count: number;
}

const EMPTY_COUNTS: DriftCounts = { ok: 0, moved: 0, missing: 0, ambiguous: 0, unresolved: 0 };

// Flatten the per-feature dependency trees into a deduped node set + directed
// flow edges. Roots are launch points; their descendants (to MAX_DEPTH) are the
// flow spine. `anchoredIds` marks nodes that a curated anchor resolved to.
function flattenTrees(
  trees: RawNode[],
  anchoredIds: Set<string>,
): { nodes: FeatureNode[]; edges: { source: string; target: string }[] } {
  const nodeMap = new Map<string, FeatureNode>();
  const edgeSet = new Set<string>();
  const edges: { source: string; target: string }[] = [];

  const labelFor = (n: RawNode) => (n.class_name ? `${n.class_name}::${n.name}` : n.name);

  const walk = (n: RawNode, depth: number, isRoot: boolean) => {
    if (nodeMap.size >= MAX_NODES) return;
    const id = `m${n.method_id}`;
    const existing = nodeMap.get(id);
    if (existing) {
      if (isRoot) existing.isLaunch = true;
    } else {
      nodeMap.set(id, { id, label: labelFor(n), filePath: n.file_path, line: n.line_number, isLaunch: isRoot, anchored: anchoredIds.has(id) });
    }
    if (depth >= MAX_DEPTH || !n.children) return;
    for (const c of n.children) {
      if (c.is_external) continue;
      const cid = `m${c.method_id}`;
      const key = `${id}->${cid}`;
      if (!edgeSet.has(key)) {
        edgeSet.add(key);
        edges.push({ source: id, target: cid });
      }
      walk(c, depth + 1, false);
    }
  };

  for (const t of trees) walk(t, 0, true);
  return { nodes: Array.from(nodeMap.values()), edges };
}

export interface RepoFeatureViewHandle { fit: () => void }

const RepoFeatureView = React.forwardRef(function RepoFeatureView(props: { repoId: number }, ref: React.Ref<RepoFeatureViewHandle>) {
  const { repoId } = props;
  const user = useAppSelector(selectUser);
  const dispatch = useAppDispatch();
  // Feature to restore on first load (from the persisted location), captured once
  // at mount before our own record effect overwrites it.
  const navLocation = useAppSelector(selectNavLocation);
  const desiredFeatureRef = React.useRef<string | null | undefined>(undefined);
  if (desiredFeatureRef.current === undefined) desiredFeatureRef.current = navLocation.featureId ?? null;

  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const [containerEl, setContainerEl] = React.useState<HTMLDivElement | null>(null);
  const sigmaRef = React.useRef<any>(null);
  const graphRef = React.useRef<any>(null);
  const selectedRef = React.useRef<string | undefined>(undefined);

  // Expose a fit/reset for the shared toolbar's "Fit graph to view" button.
  React.useImperativeHandle(ref, () => ({
    fit: () => { try { sigmaRef.current?.getCamera().animatedReset(); } catch { /* noop */ } },
  }), []);

  const [status, setStatus] = React.useState<"loading" | "done" | "failed">("loading");
  const [data, setData] = React.useState<{ nodes: FeatureNode[]; edges: { source: string; target: string }[] }>({ nodes: [], edges: [] });
  const [launchCount, setLaunchCount] = React.useState(0);
  const [anchoredCount, setAnchoredCount] = React.useState(0);
  const [counts, setCounts] = React.useState<DriftCounts>(EMPTY_COUNTS);
  const [driftRows, setDriftRows] = React.useState<Anchor[]>([]);
  const [showDrift, setShowDrift] = React.useState(false);
  const [reanchoring, setReanchoring] = React.useState(false);
  const [selected, setSelected] = React.useState<FeatureNode | null>(null);
  const [bundles, setBundles] = React.useState<BundleInfo[]>([]);
  const [colorByBundle, setColorByBundle] = React.useState(false);
  const [showBundles, setShowBundles] = React.useState(false);
  // Manual per-node colours (shared with STRUCTURAL via node_colors). pov/hidden
  // are kept in refs so saving colours doesn't clobber the other settings.
  const [nodeColors, setNodeColors] = React.useState<Record<string, string>>({});
  const povRef = React.useRef<string>("usage");
  const hiddenDirsRef = React.useRef<string[]>([]);
  // Right-click menu + "show code" modal.
  const [ctxMenu, setCtxMenu] = React.useState<{ x: number; y: number; node: FeatureNode } | null>(null);
  const [codeNode, setCodeNode] = React.useState<FeatureNode | null>(null);
  const [codeBody, setCodeBody] = React.useState("");
  const [codeLoading, setCodeLoading] = React.useState(false);
  // Single-feature focus: isolate one launch point's subtree.
  const [focusRoot, setFocusRoot] = React.useState<string | null>(null);
  // Hover path-highlight: node under cursor + the set that can REACH it.
  const hoveredRef = React.useRef<string | undefined>(undefined);
  const hoverReachRef = React.useRef<Set<string>>(new Set());
  const incomingRef = React.useRef<Map<string, string[]>>(new Map()); // target -> sources
  const outgoingRef = React.useRef<Map<string, string[]>>(new Map()); // source -> targets
  // Second selection (shift/⌘-click): the connecting path to the primary
  // selection is reddened persistently.
  const secondRef = React.useRef<string | undefined>(undefined);
  const [second, setSecond] = React.useState<FeatureNode | null>(null);
  const redEdgeKeysRef = React.useRef<Set<string>>(new Set()); // "src->tgt" keys
  // Bottom-right "flow code" reader: code of each method launch→destination.
  const [pathMin, setPathMin] = React.useState(false);
  const [pathLoading, setPathLoading] = React.useState(false);
  const [pathCode, setPathCode] = React.useState<{ id: string; label: string; filePath?: string | null; line?: number; code: string }[]>([]);
  const [expandedSteps, setExpandedSteps] = React.useState<Set<string>>(new Set());
  const pathTokenRef = React.useRef(0);
  // Edge-hover guard tooltip (only while a node is selected).
  const hoveredEdgeRef = React.useRef<string | undefined>(undefined);
  const [edgeGuard, setEdgeGuard] = React.useState<{
    x: number; y: number; loading: boolean; caller: string; callee: string;
    sites: { line: number; file_path?: string | null; guard?: string | null }[]; unconditional: boolean;
  } | null>(null);

  const assignColor = React.useCallback((id: string, color: string | null) => {
    setNodeColors((prev) => {
      const next = { ...prev };
      if (color === null) delete next[id]; else next[id] = color;
      if (user) {
        apiSaveGraphSettings(user.default_organization_id, user.default_workspace_id, repoId, povRef.current, hiddenDirsRef.current, next).catch(() => { /* ignore */ });
      }
      return next;
    });
  }, [user, repoId]);

  const showCode = React.useCallback((node: FeatureNode) => {
    if (!user) return;
    const methodId = Number(node.id.replace(/^m/, ""));
    setCtxMenu(null);
    setCodeNode(node);
    setCodeBody("");
    setCodeLoading(true);
    apiMethodCode(user.default_organization_id, user.default_workspace_id, repoId, methodId)
      .then((r: any) => setCodeBody(r?.data?.code ?? "// (no source available)"))
      .catch(() => setCodeBody("// (could not load source)"))
      .finally(() => setCodeLoading(false));
  }, [user, repoId]);

  // file path -> { color, name }, first matching bundle wins for tinting.
  const pathToBundle = React.useMemo(() => {
    const map = new Map<string, { color: string; name: string }>();
    bundles.forEach((b, i) => {
      const color = b.color || BUNDLE_FALLBACK[i % BUNDLE_FALLBACK.length];
      for (const p of b.file_paths) {
        if (!map.has(p)) map.set(p, { color, name: b.name });
      }
    });
    return map;
  }, [bundles]);

  const colorFor = React.useCallback(
    (n: FeatureNode): string => {
      // Manual colour wins, then the bundle layer, then the default tier colour.
      if (nodeColors[n.id]) return nodeColors[n.id];
      if (colorByBundle) {
        return (n.filePath && pathToBundle.get(n.filePath)?.color) || COLOR_UNBUNDLED;
      }
      return n.anchored ? COLOR_ANCHORED : n.isLaunch ? COLOR_LAUNCH : COLOR_FLOW;
    },
    [nodeColors, colorByBundle, pathToBundle],
  );

  // When focused on one feature, render only the subtree forward-reachable from
  // that launch point. Otherwise render everything.
  const viewData = React.useMemo(() => {
    if (!focusRoot) return data;
    const out = new Map<string, string[]>();
    for (const e of data.edges) {
      const arr = out.get(e.source);
      if (arr) arr.push(e.target); else out.set(e.source, [e.target]);
    }
    const keep = new Set<string>([focusRoot]);
    const q = [focusRoot];
    while (q.length) {
      const n = q.shift()!;
      for (const t of out.get(n) ?? []) if (!keep.has(t)) { keep.add(t); q.push(t); }
    }
    return {
      nodes: data.nodes.filter((n) => keep.has(n.id)),
      edges: data.edges.filter((e) => keep.has(e.source) && keep.has(e.target)),
    };
  }, [data, focusRoot]);

  // Incoming/outgoing edge indexes for the reachability walks.
  React.useEffect(() => {
    const inc = new Map<string, string[]>();
    const out = new Map<string, string[]>();
    for (const e of viewData.edges) {
      const i = inc.get(e.target); if (i) i.push(e.source); else inc.set(e.target, [e.source]);
      const o = out.get(e.source); if (o) o.push(e.target); else out.set(e.source, [e.target]);
    }
    incomingRef.current = inc;
    outgoingRef.current = out;
  }, [viewData]);

  // Recompute the persistent red path whenever selection changes:
  //   • one node selected   → the paths that REACH it (reverse-reachability)
  //   • two nodes selected   → the connecting path between them
  // Stays until the selection changes or the stage is clicked.
  React.useEffect(() => {
    const a = selectedRef.current;
    const b = secondRef.current;
    const keys = new Set<string>();
    const reach = (start: string, adj: Map<string, string[]>): Set<string> => {
      const s = new Set<string>([start]);
      const q = [start];
      while (q.length) {
        const n = q.shift()!;
        for (const m of adj.get(n) ?? []) if (!s.has(m)) { s.add(m); q.push(m); }
      }
      return s;
    };
    if (a && b && a !== b) {
      // Connecting path: try src→dst both ways, keep whichever connects.
      const tryDir = (src: string, dst: string): Set<string> | null => {
        const fwd = reach(src, outgoingRef.current);
        if (!fwd.has(dst)) return null;
        const bwd = reach(dst, incomingRef.current);
        const between = new Set<string>();
        fwd.forEach((n) => { if (bwd.has(n)) between.add(n); });
        const ks = new Set<string>();
        for (const e of viewData.edges) if (between.has(e.source) && between.has(e.target)) ks.add(`${e.source}->${e.target}`);
        return ks;
      };
      const ks = tryDir(a, b) ?? tryDir(b, a);
      if (ks) ks.forEach((k) => keys.add(k));
    } else if (a) {
      // All edges on a path that reaches the selected node.
      const reachSet = reach(a, incomingRef.current);
      for (const e of viewData.edges) if (reachSet.has(e.target)) keys.add(`${e.source}->${e.target}`);
    }
    redEdgeKeysRef.current = keys;
    try { sigmaRef.current?.refresh(); } catch { /* noop */ }
  }, [selected, second, viewData]);

  // Fetch the code of every method on the path launch→selected, in flow order.
  React.useEffect(() => {
    const a = selectedRef.current;
    if (!user || !a || secondRef.current) { setPathCode([]); setPathLoading(false); return; }
    // Ancestors + self (everything that reaches the selected node).
    const reachSet = new Set<string>([a]);
    const q = [a];
    while (q.length) {
      const n = q.shift()!;
      for (const s of incomingRef.current.get(n) ?? []) if (!reachSet.has(s)) { reachSet.add(s); q.push(s); }
    }
    // Depth from the focus root (over outgoing) to order launch→destination.
    const depth = new Map<string, number>();
    if (focusRoot) {
      depth.set(focusRoot, 0);
      const dq = [focusRoot];
      while (dq.length) {
        const n = dq.shift()!;
        for (const t of outgoingRef.current.get(n) ?? []) if (!depth.has(t)) { depth.set(t, (depth.get(n) ?? 0) + 1); dq.push(t); }
      }
    }
    const ordered = viewData.nodes
      .filter((n) => reachSet.has(n.id))
      .sort((x, y) => (depth.get(x.id) ?? 999) - (depth.get(y.id) ?? 999))
      .slice(0, 30);

    const token = ++pathTokenRef.current;
    setPathLoading(true);
    Promise.all(ordered.map((n) =>
      apiMethodCode(user.default_organization_id, user.default_workspace_id, repoId, Number(n.id.replace(/^m/, "")))
        .then((r: any) => ({ id: n.id, label: n.label, filePath: n.filePath, line: n.line, code: r?.data?.code ?? "// (no source)" }))
        .catch(() => ({ id: n.id, label: n.label, filePath: n.filePath, line: n.line, code: "// (could not load)" })),
    )).then((res) => {
      if (pathTokenRef.current === token) { setPathCode(res); setExpandedSteps(new Set()); setPathLoading(false); }
    });
  }, [selected, second, viewData, focusRoot, user, repoId]);

  const focusLabel = focusRoot ? data.nodes.find((n) => n.id === focusRoot)?.label : undefined;

  // Record the focused feature / selected node as the current location.
  React.useEffect(() => {
    dispatch(setNavLocation({
      kind: "graph", networkMode: "feature", networkRepoId: repoId,
      featureId: focusRoot, featureLabel: focusLabel ?? null, selectedNode: selected?.id ?? null,
    }));
  }, [focusRoot, focusLabel, selected, repoId, dispatch]);

  // The path launch→node (ancestors + self), ordered by depth from the focus root.
  const pathNodesTo = React.useCallback((id: string): FeatureNode[] => {
    const reachSet = new Set<string>([id]);
    const q = [id];
    while (q.length) {
      const n = q.shift()!;
      for (const s of incomingRef.current.get(n) ?? []) if (!reachSet.has(s)) { reachSet.add(s); q.push(s); }
    }
    const depth = new Map<string, number>();
    if (focusRoot) {
      depth.set(focusRoot, 0);
      const dq = [focusRoot];
      while (dq.length) {
        const n = dq.shift()!;
        for (const t of outgoingRef.current.get(n) ?? []) if (!depth.has(t)) { depth.set(t, (depth.get(n) ?? 0) + 1); dq.push(t); }
      }
    }
    return viewData.nodes.filter((n) => reachSet.has(n.id)).sort((x, y) => (depth.get(x.id) ?? 999) - (depth.get(y.id) ?? 999));
  }, [viewData, focusRoot]);

  // Copy the whole path (function names + locations) launch→node as plain text
  // for pasting into Claude.
  const copyPath = React.useCallback((node: FeatureNode) => {
    setCtxMenu(null);
    const nodes = pathNodesTo(node.id);
    const root = nodes[0]?.label ?? node.label;
    const lines = nodes.map((n, i) => `${i + 1}. ${n.label}  (${n.filePath ?? "?"}${n.line ? `:${n.line}` : ""})`);
    const text = `Call path: ${root} → ${node.label}\n${lines.join("\n")}`;
    try { navigator.clipboard?.writeText(text); } catch { /* clipboard unavailable */ }
  }, [pathNodesTo]);

  // Features (launch points) grouped by bundle for the top-left picker.
  const featureGroups = React.useMemo(() => {
    const groups = new Map<string, { name: string; color: string; items: FeatureNode[] }>();
    for (const n of data.nodes) {
      if (!n.isLaunch) continue;
      const b = n.filePath ? pathToBundle.get(n.filePath) : undefined;
      const key = b?.name ?? "Unbundled";
      const color = b?.color ?? COLOR_UNBUNDLED;
      if (!groups.has(key)) groups.set(key, { name: key, color, items: [] });
      groups.get(key)!.items.push(n);
    }
    const arr = Array.from(groups.values());
    arr.forEach((g) => g.items.sort((a, b) => a.label.localeCompare(b.label)));
    arr.sort((a, b) => (a.name === "Unbundled" ? 1 : b.name === "Unbundled" ? -1 : a.name.localeCompare(b.name)));
    return arr;
  }, [data, pathToBundle]);

  // Show one feature at a time. On the first load with data, restore the saved
  // feature (fallback to the first if it's gone); thereafter focus the first.
  React.useEffect(() => {
    if (data.nodes.length === 0) { setFocusRoot(null); return; }
    const wanted = desiredFeatureRef.current;
    desiredFeatureRef.current = null;
    const valid = wanted != null && data.nodes.some((n) => n.id === wanted && n.isLaunch);
    const first = data.nodes.find((n) => n.isLaunch);
    setFocusRoot(valid ? (wanted as string) : (first ? first.id : null));
  }, [data]);

  const setRef = React.useCallback((el: HTMLDivElement | null) => {
    containerRef.current = el;
    setContainerEl(el);
  }, []);

  const load = React.useCallback(() => {
    if (!user) return;
    setStatus("loading");
    Promise.all([
      apiFeatureTrees(user.default_organization_id, user.default_workspace_id, repoId),
      apiListAnchors(user.default_organization_id, user.default_workspace_id, repoId),
      apiListBundles(user.default_organization_id, user.default_workspace_id, repoId),
      apiGetGraphSettings(user.default_organization_id, user.default_workspace_id, repoId),
    ])
      .then(([treesResp, anchorsResp, bundlesResp, settingsResp]: any[]) => {
        const trees: RawNode[] = (treesResp?.data?.trees ?? []).map((t: any) => t.tree).filter(Boolean);
        const anchors: Anchor[] = anchorsResp?.data?.anchors ?? [];
        setBundles(bundlesResp?.data?.bundles ?? []);
        const gs = settingsResp?.data ?? {};
        povRef.current = gs.pov ?? "usage";
        hiddenDirsRef.current = gs.hidden_dirs ?? [];
        setNodeColors(gs.node_colors ?? {});
        const anchoredIds = new Set(
          anchors
            .filter((a) => a.drift_state === "ok" && (a.owner_kind === "launch" || a.owner_kind === "feature") && a.resolved_method_id != null)
            .map((a) => `m${a.resolved_method_id}`),
        );
        const flat = flattenTrees(trees, anchoredIds);
        setData(flat);
        setLaunchCount(flat.nodes.filter((n) => n.isLaunch).length);
        setAnchoredCount(anchoredIds.size);
        setCounts(anchorsResp?.data?.counts ?? EMPTY_COUNTS);
        setDriftRows(anchors.filter((a) => a.drift_state !== "ok"));
        setStatus("done");
      })
      .catch(() => setStatus("failed"));
  }, [user, repoId]);

  React.useEffect(() => { load(); }, [load]);

  const handleReanchor = React.useCallback(() => {
    if (!user) return;
    setReanchoring(true);
    apiReanchor(user.default_organization_id, user.default_workspace_id, repoId)
      .then(() => load())
      .finally(() => setReanchoring(false));
  }, [user, repoId, load]);

  const [clearing, setClearing] = React.useState(false);
  const handleClearFeatures = React.useCallback(() => {
    if (!user) return;
    if (!window.confirm(
      "Remove all features?\n\nThis un-marks every function designated as a feature and deletes their anchors, clearing the FEATURE graph. Bundles are kept. You can re-mark functions afterwards.",
    )) return;
    setClearing(true);
    apiClearFeatures(user.default_organization_id, user.default_workspace_id, repoId)
      .then(() => load())
      .finally(() => setClearing(false));
  }, [user, repoId, load]);


  // Tear sigma down on unmount (e.g. switching back to STRUCTURAL).
  React.useEffect(() => () => {
    if (sigmaRef.current) { try { sigmaRef.current.kill(); } catch { /* noop */ } sigmaRef.current = null; }
    graphRef.current = null;
  }, []);

  // Build/refresh the graph once the container exists and data is loaded.
  React.useEffect(() => {
    const container = containerEl;
    if (!container || status !== "done") return;

    if (!sigmaRef.current) {
      try {
        const graph = new Graph({ type: "directed", multi: false });
        const sigma = new Sigma(graph, container, {
          renderLabels: true,
          labelColor: { color: "#000000" },
          labelFont: "ui-monospace, monospace",
          labelSize: 12,
          labelRenderedSizeThreshold: 6,
          enableEdgeEvents: true,
          defaultDrawNodeLabel: drawNodeLabel,
          defaultDrawNodeHover: drawNodeHover,
          defaultNodeColor: COLOR_FLOW,
          defaultEdgeColor: COLOR_EDGE,
          minCameraRatio: 0.02,
          maxCameraRatio: 20,
          nodeReducer: (node: string, attrs: any) => {
            let a = attrs;
            if (node === selectedRef.current || node === secondRef.current) a = { ...a, color: COLOR_SELECTED, zIndex: 3, forceLabel: true, sel: true };
            if (hoveredRef.current) {
              if (hoverReachRef.current.has(node)) a = { ...a, zIndex: 2, forceLabel: true };
              else a = { ...a, color: "#2a3142", label: "" }; // off-path: dim + hide label
            }
            return a;
          },
          edgeReducer: (edge: string, attrs: any) => {
            const g = graphRef.current;
            // Persistent red, double-thick: the path to the selected node (or the
            // connecting path between two selections). Stays until selection clears.
            if (g && redEdgeKeysRef.current.size && redEdgeKeysRef.current.has(`${g.source(edge)}->${g.target(edge)}`)) {
              return { ...attrs, color: "#ef4444", size: Math.max(3, (attrs.size || 1.2) * 2), zIndex: 4 };
            }
            if (!hoveredRef.current) return attrs;
            // An edge u->v is on a path to the hovered node iff v can reach it.
            const onPath = g && hoverReachRef.current.has(g.target(edge));
            return onPath ? { ...attrs, color: "#e0a82e", size: 2, zIndex: 1 } : { ...attrs, color: "#1b2030" };
          },
        });
        sigma.on("enterNode", (e: { node: string }) => {
          hoveredRef.current = e.node;
          // Reverse BFS: everything that can reach the hovered node.
          const seen = new Set<string>([e.node]);
          const q = [e.node];
          while (q.length) {
            const n = q.shift()!;
            for (const s of incomingRef.current.get(n) ?? []) if (!seen.has(s)) { seen.add(s); q.push(s); }
          }
          hoverReachRef.current = seen;
          sigma.refresh();
        });
        sigma.on("leaveNode", () => {
          hoveredRef.current = undefined;
          hoverReachRef.current = new Set();
          sigma.refresh();
        });
        // Edge hover → "what conditions get here" (only once a node is selected).
        sigma.on("enterEdge", (e: any) => {
          if (!selectedRef.current) return;
          const g = graphRef.current;
          if (!g) return;
          hoveredEdgeRef.current = e.edge;
          const src = g.source(e.edge);
          const tgt = g.target(e.edge);
          const orig = e?.event?.original;
          setEdgeGuard({
            x: orig?.clientX ?? 0, y: orig?.clientY ?? 0, loading: true,
            caller: g.getNodeAttribute(src, "label"), callee: g.getNodeAttribute(tgt, "label"),
            sites: [], unconditional: false,
          });
          if (user) {
            apiEdgeGuards(user.default_organization_id, user.default_workspace_id, repoId, Number(src.replace(/^m/, "")), Number(tgt.replace(/^m/, "")))
              .then((r: any) => {
                if (hoveredEdgeRef.current !== e.edge) return;
                setEdgeGuard((prev) => prev ? { ...prev, loading: false, sites: r?.data?.sites ?? [], unconditional: !!r?.data?.unconditional } : prev);
              })
              .catch(() => { if (hoveredEdgeRef.current === e.edge) setEdgeGuard(null); });
          }
        });
        sigma.on("leaveEdge", (e: any) => {
          if (hoveredEdgeRef.current === e.edge) { hoveredEdgeRef.current = undefined; setEdgeGuard(null); }
        });
        sigma.on("clickNode", (e: any) => {
          const g = graphRef.current;
          const fnode = g && g.hasNode(e.node) ? (g.getNodeAttribute(e.node, "fnode") as FeatureNode) : null;
          const orig = e?.event?.original;
          const additive = !!(orig && (orig.shiftKey || orig.metaKey || orig.ctrlKey));
          if (additive && selectedRef.current && e.node !== selectedRef.current) {
            // Second selection: light up the connecting path in red.
            secondRef.current = e.node;
            setSecond(fnode);
          } else {
            selectedRef.current = e.node;
            setSelected(fnode);
            secondRef.current = undefined;
            setSecond(null);
          }
          sigma.refresh();
        });
        sigma.on("clickStage", () => {
          selectedRef.current = undefined; setSelected(null);
          secondRef.current = undefined; setSecond(null);
          redEdgeKeysRef.current = new Set();
          setEdgeGuard(null);
          sigma.refresh();
        });
        sigma.on("rightClickNode", (e: any) => {
          const orig = e?.event?.original;
          if (orig && typeof orig.preventDefault === "function") orig.preventDefault();
          const g = graphRef.current;
          const node = g && g.hasNode(e.node) ? (g.getNodeAttribute(e.node, "fnode") as FeatureNode) : null;
          if (node) setCtxMenu({ x: orig?.clientX ?? 0, y: orig?.clientY ?? 0, node });
        });
        graphRef.current = graph;
        sigmaRef.current = sigma;
      } catch {
        setStatus("failed");
        return;
      }
    }

    const graph = graphRef.current;
    if (!graph) return;
    graph.clear();

    for (const n of viewData.nodes) {
      graph.addNode(n.id, {
        x: (Math.random() - 0.5) * 300,
        y: (Math.random() - 0.5) * 300,
        size: n.isLaunch ? 9 : 4,
        label: n.label,
        color: colorFor(n),
        forceLabel: true,
        launch: n.isLaunch,
        fnode: n,
      });
    }
    for (const e of viewData.edges) {
      if (graph.hasNode(e.source) && graph.hasNode(e.target) && !graph.hasEdge(e.source, e.target)) {
        graph.addEdge(e.source, e.target, { size: 1.2, color: COLOR_EDGE });
      }
    }

    if (graph.order > 0) {
      const settings = forceAtlas2.inferSettings(graph);
      forceAtlas2.assign(graph, { iterations: graph.order > 300 ? 200 : 400, settings });
    }
    sigmaRef.current.refresh();
    try { sigmaRef.current.getCamera().animatedReset(); } catch { /* noop */ }
    // colorFor intentionally omitted — the recolor effect below handles toggles
    // so we don't re-run the layout when only the tint changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerEl, status, viewData]);

  // Re-tint nodes when the bundle layer toggles, WITHOUT re-running layout.
  React.useEffect(() => {
    const graph = graphRef.current;
    if (!graph || !sigmaRef.current) return;
    graph.forEachNode((id: string, attrs: any) => {
      const n = attrs.fnode as FeatureNode | undefined;
      if (n) graph.setNodeAttribute(id, "color", colorFor(n));
    });
    sigmaRef.current.refresh();
  }, [colorFor]);

  const driftTotal = counts.moved + counts.missing + counts.ambiguous + counts.unresolved;

  return (
    <Box sx={{ position: "absolute", inset: 0, bgcolor: "#0b1220", zIndex: 10 }} onContextMenu={(e) => e.preventDefault()}>
      <div ref={setRef} style={{ position: "absolute", inset: 0 }} />

      {/* Top toolbar: counts + drift + re-anchor */}
      {status === "done" && (
        <Box sx={{ position: "absolute", top: 8, left: 12, right: 12, display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          <Typography variant="body2" sx={{ color: "#8893b5", fontFamily: "monospace", fontSize: 11 }}>
            {launchCount} launch · {anchoredCount} anchored · {viewData.nodes.length} nodes · {viewData.edges.length} edges
          </Typography>
          {focusRoot && (
            <Chip
              size="small"
              label={`Focused: ${focusLabel ?? focusRoot}`}
              onDelete={() => setFocusRoot(null)}
              sx={{ bgcolor: "#1c3a73", color: "#fff", maxWidth: 320 }}
            />
          )}
          {driftTotal > 0 && (
            <Chip
              size="small"
              color="warning"
              label={`${driftTotal} anchor${driftTotal === 1 ? "" : "s"} need re-anchoring`}
              onClick={() => setShowDrift((v) => !v)}
              sx={{ cursor: "pointer" }}
            />
          )}
          <Box sx={{ flex: 1 }} />
          {bundles.length > 0 && (
            <Button
              size="small"
              variant={colorByBundle ? "contained" : "outlined"}
              onClick={() => setColorByBundle((v) => !v)}
              sx={{ textTransform: "none", ...(colorByBundle ? { bgcolor: "#1c3a73" } : { color: "#9fb4ff", borderColor: "#33406a" }) }}
            >
              Color by bundle
            </Button>
          )}
          <Button
            size="small"
            variant="outlined"
            onClick={() => setShowBundles((v) => !v)}
            sx={{ color: "#9fb4ff", borderColor: "#33406a", textTransform: "none" }}
          >
            Bundles ({bundles.length})
          </Button>
          <Button
            size="small"
            variant="outlined"
            disabled={reanchoring}
            onClick={handleReanchor}
            sx={{ color: "#9fb4ff", borderColor: "#33406a", textTransform: "none" }}
          >
            {reanchoring ? "Re-anchoring…" : "Re-anchor"}
          </Button>
          {data.nodes.length > 0 && (
            <Button
              size="small"
              variant="outlined"
              color="error"
              disabled={clearing}
              onClick={handleClearFeatures}
              sx={{ textTransform: "none" }}
            >
              {clearing ? "Clearing…" : "Clear features"}
            </Button>
          )}
        </Box>
      )}

      {/* Bundles panel (districts) */}
      {showBundles && (
        <Paper elevation={6} sx={{ position: "absolute", bottom: 64, left: 320, width: 320, maxHeight: 300, overflow: "auto", p: 1.5, bgcolor: "#141a2b", color: "#e6ebff", zIndex: 11 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Bundles</Typography>
          {bundles.length === 0 ? (
            <Typography variant="caption" sx={{ color: "#8893b5" }}>
              No bundles yet. Create one with a path-glob or name-prefix rule.
            </Typography>
          ) : (
            bundles.map((b, i) => (
              <Box key={b.id} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: "2px", bgcolor: b.color || BUNDLE_FALLBACK[i % BUNDLE_FALLBACK.length], flexShrink: 0 }} />
                <Typography variant="body2" sx={{ flex: 1 }}>{b.name}</Typography>
                <Typography variant="caption" sx={{ color: "#8893b5" }}>{b.file_count} files</Typography>
              </Box>
            ))
          )}
        </Paper>
      )}

      {/* Drift work-list (toggled by the chip) */}
      {/* Top-left feature picker — choose one feature at a time, grouped by bundle */}
      {status === "done" && featureGroups.length > 0 && (
        <Paper elevation={6} sx={{ position: "absolute", top: 44, left: 12, width: 300, maxHeight: "70%", overflow: "auto", p: 1, bgcolor: "#141a2b", color: "#e6ebff", zIndex: 12 }}>
          <Typography variant="subtitle2" sx={{ px: 0.5, mb: 0.5 }}>Features</Typography>
          {featureGroups.map((g) => (
            <Box key={g.name}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.75, mb: 0.25, px: 0.5 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: "2px", bgcolor: g.color, flexShrink: 0 }} />
                <Typography variant="caption" sx={{ color: "#8893b5" }}>{g.name} · {g.items.length}</Typography>
              </Box>
              {g.items.map((it) => (
                <Box
                  key={it.id}
                  onClick={() => setFocusRoot(it.id)}
                  sx={{ px: 1, py: 0.5, borderRadius: 1, cursor: "pointer", bgcolor: focusRoot === it.id ? "#1c3a73" : "transparent", "&:hover": { bgcolor: "#1f2740" }, wordBreak: "break-all" }}
                >
                  <Typography variant="body2">{it.label}</Typography>
                </Box>
              ))}
            </Box>
          ))}
        </Paper>
      )}

      {showDrift && driftRows.length > 0 && (
        <Paper elevation={6} sx={{ position: "absolute", top: 44, left: 324, width: 360, maxHeight: 320, overflow: "auto", p: 1.5, bgcolor: "#141a2b", color: "#e6ebff", zIndex: 11 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Anchors needing attention</Typography>
          {driftRows.map((a) => (
            <Box key={a.id} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
              <Chip
                size="small"
                label={a.drift_state}
                sx={{
                  bgcolor: a.drift_state === "missing" ? "#7a1f1f" : a.drift_state === "moved" ? "#7a5a18" : "#3a3f55",
                  color: "#fff", minWidth: 72,
                }}
              />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                  {a.symbol_name ?? a.file_path.split("/").pop()}
                </Typography>
                <Typography variant="caption" sx={{ color: "#8893b5", wordBreak: "break-all" }}>
                  {a.owner_kind} · {a.file_path}
                </Typography>
              </Box>
            </Box>
          ))}
        </Paper>
      )}

      {/* Legend — reflects the active color layer */}
      <Paper elevation={4} sx={{ position: "absolute", bottom: 12, left: 320, p: 1.25, bgcolor: "#141a2b", color: "#e6ebff", display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
        {colorByBundle ? (
          <>
            <Typography variant="caption" sx={{ color: "#8893b5" }}>Fill = bundle · larger = launch point</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: COLOR_UNBUNDLED }} />
              <Typography variant="caption">Unbundled</Typography>
            </Box>
          </>
        ) : (
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: COLOR_ANCHORED }} />
              <Typography variant="caption">Anchored launch</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: COLOR_LAUNCH }} />
              <Typography variant="caption">Auto launch</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: COLOR_FLOW }} />
              <Typography variant="caption">Flow node</Typography>
            </Box>
          </>
        )}
      </Paper>

      {status === "loading" && (
        <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", color: "#cfd8ff" }}>
          <CircularProgress color="inherit" />
          <Typography sx={{ mt: 2 }}>Loading feature overlay…</Typography>
        </Box>
      )}

      {status === "failed" && (
        <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", color: "#ffb4b4" }}>
          <Typography>Could not load the feature overlay.</Typography>
          <Button onClick={load} variant="contained" sx={{ mt: 2 }}>Retry</Button>
        </Box>
      )}

      {status === "done" && data.nodes.length === 0 && (
        <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", color: "#cfd8ff", px: 4, textAlign: "center" }}>
          <Typography variant="h6">No features yet</Typography>
          <Typography variant="body2" sx={{ mt: 1, color: "#8893b5", maxWidth: 520 }}>
            FEATURE mode spiders out the call flow of functions you designate as
            features. In STRUCTURAL mode, find a function (expand a file or search),
            right-click it, and choose “Mark as feature” — or use the “Suggested
            entries” list to confirm candidates. Bundles and policy layers arrive in
            later phases.
          </Typography>
          {driftRows.length > 0 && (
            <Button onClick={() => setShowDrift(true)} sx={{ mt: 2, color: "#9fb4ff", textTransform: "none" }}>
              {driftRows.length} anchor{driftRows.length === 1 ? "" : "s"} not resolving — show
            </Button>
          )}
        </Box>
      )}

      {/* Selected node detail */}
      {selected && (
        <Paper elevation={6} sx={{ position: "absolute", top: 44, right: 12, width: 320, p: 2, bgcolor: "#141a2b", color: "#e6ebff", zIndex: 11 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}>
            <Chip size="small" label={selected.isLaunch ? "launch point" : "flow node"} sx={{ bgcolor: selected.isLaunch ? "#7a3318" : "#0f5a44", color: "#fff" }} />
            {selected.anchored && <Chip size="small" label="anchored" sx={{ bgcolor: "#6b4e10", color: "#fff" }} />}
          </Box>
          <Typography variant="subtitle2" sx={{ wordBreak: "break-all" }}>{selected.label}</Typography>
          {selected.filePath && (
            <Typography variant="body2" sx={{ color: "#8893b5", wordBreak: "break-all", mt: 0.5 }}>{selected.filePath}</Typography>
          )}
          {second ? (
            <Typography variant="caption" sx={{ color: "#ef9a9a", display: "block", mt: 0.5, wordBreak: "break-all" }}>
              ↔ connecting path to {second.label} shown in red
            </Typography>
          ) : (
            <Typography variant="caption" sx={{ color: "#8893b5", display: "block", mt: 0.5 }}>
              Shift/⌘-click another node to red the connecting path
            </Typography>
          )}

          {/* Manual node colour */}
          <Typography variant="caption" sx={{ color: "#8893b5", display: "block", mt: 1.5, mb: 0.5 }}>Colour</Typography>
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", alignItems: "center" }}>
            {GROUP_COLORS.map((c) => (
              <Box
                key={c}
                onClick={() => assignColor(selected.id, c)}
                sx={{ width: 18, height: 18, borderRadius: "3px", bgcolor: c, cursor: "pointer", border: nodeColors[selected.id] === c ? "2px solid #fff" : "1px solid rgba(0,0,0,0.4)" }}
              />
            ))}
            <Box
              onClick={() => assignColor(selected.id, null)}
              title="Clear colour"
              sx={{ width: 18, height: 18, borderRadius: "3px", cursor: "pointer", border: "1px solid #5a6488", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, color: "#8893b5" }}
            >
              ×
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 1, mt: 1.5, flexWrap: "wrap" }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<CodeIcon />}
              onClick={() => showCode(selected)}
              sx={{ color: "#9fb4ff", borderColor: "#33406a", textTransform: "none" }}
            >
              Show code
            </Button>
            {selected.isLaunch && focusRoot !== selected.id && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => setFocusRoot(selected.id)}
                sx={{ color: "#9fe6b8", borderColor: "#1d6e56", textTransform: "none" }}
              >
                Focus this feature
              </Button>
            )}
          </Box>
        </Paper>
      )}

      {/* Right-click context menu */}
      <Menu
        open={ctxMenu !== null}
        onClose={() => setCtxMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={ctxMenu ? { top: ctxMenu.y, left: ctxMenu.x } : undefined}
        PaperProps={{ sx: { bgcolor: "#1a2236", color: "#e6ebff" } }}
      >
        <MenuItem onClick={() => ctxMenu && showCode(ctxMenu.node)}>
          <CodeIcon fontSize="small" style={{ marginRight: 8 }} /> Show code!
        </MenuItem>
        <MenuItem onClick={() => ctxMenu && copyPath(ctxMenu.node)}>
          <ContentCopyIcon fontSize="small" style={{ marginRight: 8 }} /> Copy path to clipboard
        </MenuItem>
      </Menu>

      {/* Show-code modal */}
      <Dialog
        open={codeNode !== null}
        onClose={() => setCodeNode(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { bgcolor: "#0e1422", color: "#e6ebff" } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", py: 1.25 }}>
          <span style={{ flex: 1, wordBreak: "break-all" }}>{codeNode?.label}</span>
          <IconButton size="small" sx={{ color: "#8893b5" }} onClick={() => setCodeNode(null)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent>
          {codeNode?.filePath && <Typography variant="caption" sx={{ color: "#8893b5", wordBreak: "break-all" }}>{codeNode.filePath}</Typography>}
          <Box component="pre" sx={{ mt: 1, p: 1.5, bgcolor: "#0b1220", borderRadius: 1, overflow: "auto", fontFamily: "ui-monospace, monospace", fontSize: 12, whiteSpace: "pre", maxHeight: "60vh" }}>
            {codeLoading ? "Loading…" : codeBody}
          </Box>
        </DialogContent>
      </Dialog>

      {/* Edge-guard tooltip: conditions under which this call is reached */}
      {edgeGuard && (
        <Paper
          elevation={8}
          sx={{ position: "absolute", left: Math.min(edgeGuard.x + 14, (window.innerWidth || 1200) - 380), top: edgeGuard.y + 14, width: 340, p: 1.25, bgcolor: "#141a2b", color: "#e6ebff", zIndex: 13, pointerEvents: "none" }}
        >
          <Typography variant="caption" sx={{ color: "#8893b5", display: "block", wordBreak: "break-all" }}>
            {edgeGuard.caller} → {edgeGuard.callee}
          </Typography>
          {edgeGuard.loading ? (
            <Typography variant="body2" sx={{ mt: 0.5 }}>Loading conditions…</Typography>
          ) : edgeGuard.sites.length === 0 ? (
            <Typography variant="body2" sx={{ mt: 0.5, color: "#8893b5" }}>
              No call-site detail. Purge &amp; re-index to capture guards.
            </Typography>
          ) : (
            <Box sx={{ mt: 0.5 }}>
              {edgeGuard.unconditional && (
                <Typography variant="body2" sx={{ color: "#9fe6b8" }}>Always — reached unconditionally</Typography>
              )}
              {Array.from(new Set(edgeGuard.sites.map((s) => s.guard).filter((g): g is string => !!g && g.length > 0))).map((g, i) => (
                <Typography key={i} variant="body2" sx={{ fontFamily: "ui-monospace, monospace", fontSize: 12, color: "#e0a82e", wordBreak: "break-all", mt: 0.25 }}>
                  {g}
                </Typography>
              ))}
              <Typography variant="caption" sx={{ color: "#8893b5", display: "block", mt: 0.5 }}>
                {edgeGuard.sites.length} call site{edgeGuard.sites.length === 1 ? "" : "s"}
              </Typography>
            </Box>
          )}
        </Paper>
      )}

      {/* Bottom-right flow-code reader: code launch→destination, minimizable */}
      {selected && !second && (
        <Paper
          elevation={8}
          sx={{
            position: "absolute", right: 12, bottom: 12, zIndex: 13,
            width: pathMin ? 340 : "50vw", maxWidth: "50vw",
            height: pathMin ? "auto" : "50vh", maxHeight: "50vh",
            display: "flex", flexDirection: "column", bgcolor: "#0e1422", color: "#e6ebff",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, p: 1, borderBottom: pathMin ? "none" : "1px solid #27304d" }}>
            <CodeIcon fontSize="small" sx={{ color: "#8893b5" }} />
            <Typography variant="caption" sx={{ flex: 1, wordBreak: "break-all" }}>
              Flow code → {selected.label}{pathCode.length ? ` · ${pathCode.length} step${pathCode.length === 1 ? "" : "s"}` : ""}
            </Typography>
            <IconButton size="small" sx={{ color: "#8893b5" }} onClick={() => setPathMin((v) => !v)} title={pathMin ? "Expand" : "Minimize"}>
              <Box component="span" sx={{ fontSize: 14, lineHeight: 1 }}>{pathMin ? "▴" : "▾"}</Box>
            </IconButton>
            <IconButton
              size="small"
              sx={{ color: "#8893b5" }}
              title="Close"
              onClick={() => { selectedRef.current = undefined; setSelected(null); redEdgeKeysRef.current = new Set(); try { sigmaRef.current?.refresh(); } catch { /* noop */ } }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          {!pathMin && (
            <Box sx={{ overflow: "auto", p: 1 }}>
              {pathLoading ? (
                <Typography variant="body2" sx={{ color: "#8893b5" }}>Loading flow code…</Typography>
              ) : (
                pathCode.map((c, i) => {
                  const open = expandedSteps.has(c.id);
                  return (
                    <Box key={c.id} sx={{ mb: 0.75 }}>
                      <Box
                        onClick={() => setExpandedSteps((prev) => { const n = new Set(prev); if (n.has(c.id)) n.delete(c.id); else n.add(c.id); return n; })}
                        sx={{ display: "flex", alignItems: "baseline", gap: 0.5, cursor: "pointer", borderRadius: 1, p: 0.5, "&:hover": { bgcolor: "#141a2b" } }}
                      >
                        <Box component="span" sx={{ fontSize: 12, color: "#8893b5", width: 12, flexShrink: 0 }}>{open ? "▾" : "▸"}</Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="caption" sx={{ color: i === pathCode.length - 1 ? "#f59e0a" : "#9fe6b8", wordBreak: "break-all" }}>
                            {i + 1}. {c.label}
                          </Typography>
                          {c.filePath && (
                            <Typography variant="caption" sx={{ color: "#8893b5", display: "block", wordBreak: "break-all" }}>
                              {c.filePath}{c.line ? `:${c.line}` : ""}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                      {open && (
                        <Box component="pre" sx={{ mt: 0.5, ml: 2, p: 1, bgcolor: "#0b1220", borderRadius: 1, overflow: "auto", fontFamily: "ui-monospace, monospace", fontSize: 12, whiteSpace: "pre", maxHeight: "30vh" }}>
                          {c.code}
                        </Box>
                      )}
                    </Box>
                  );
                })
              )}
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
});

export default RepoFeatureView;
